import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'

export type AiJobKey = 'team_individual' | 'team_group' | 'cover' | 'logo'

export interface TeamMemberResult {
  memberId: string
  memberName: string
  images: string[]
}

export interface AiJob {
  status: 'running' | 'done' | 'error'
  startedAt: number
  progress?: string
  images?: string[]
  results?: TeamMemberResult[]
  total?: number
  error?: string
}

export type JobUpdate = (patch: Partial<AiJob>) => void

interface AiJobsContextValue {
  jobs: Partial<Record<AiJobKey, AiJob>>
  run: (key: AiJobKey, fn: (update: JobUpdate) => Promise<void>) => Promise<void>
  clear: (key: AiJobKey) => void
  galleryVersion: number
  bumpGallery: () => void
}

const AiJobsContext = createContext<AiJobsContextValue | undefined>(undefined)

export function AiJobsProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<Partial<Record<AiJobKey, AiJob>>>({})
  const [galleryVersion, setGalleryVersion] = useState(0)

  const run = useCallback<AiJobsContextValue['run']>(async (key, fn) => {
    setJobs((prev) => ({
      ...prev,
      [key]: {
        status: 'running',
        startedAt: Date.now(),
      },
    }))

    const update: JobUpdate = (patch) => {
      setJobs((prev) => {
        const current = prev[key]
        if (!current) return prev
        return { ...prev, [key]: { ...current, ...patch } }
      })
    }

    try {
      await fn(update)
      setJobs((prev) => {
        const current = prev[key]
        if (!current) return prev
        return { ...prev, [key]: { ...current, status: 'done' } }
      })
      setGalleryVersion((v) => v + 1)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Generation failed'
      setJobs((prev) => {
        const current = prev[key]
        if (!current) return prev
        return {
          ...prev,
          [key]: { ...current, status: 'error', error: message },
        }
      })
    }
  }, [])

  const clear = useCallback<AiJobsContextValue['clear']>((key) => {
    setJobs((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const bumpGallery = useCallback(() => {
    setGalleryVersion((v) => v + 1)
  }, [])

  return (
    <AiJobsContext.Provider value={{ jobs, run, clear, galleryVersion, bumpGallery }}>
      {children}
    </AiJobsContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAiJobs(): AiJobsContextValue {
  const ctx = useContext(AiJobsContext)
  if (!ctx) {
    throw new Error('useAiJobs must be used within AiJobsProvider')
  }
  return ctx
}
